// ============================================================
// PERRY — UI PRIMITIVES
// ensureKeyframes (the ONE way animations enter the page),
// EmberDust ambience, the CinematicLayer (letterbox / vignette /
// grain), and small shared chrome pieces.
//
// LAWS honored here:
//   • Keyframe injection law — id-guarded <style> nodes appended
//     to document.head, with the stale-<style> self-heal: if the
//     node exists but its text differs from THIS build's CSS,
//     REPLACE it (an id-only guard serves last version's frames).
//   • Never <style> blocks inside component JSX.
// ============================================================

import { GM } from "./theme";

// ── ensureKeyframes — id-guarded, self-healing ───────────────
export function ensureKeyframes(id, css) {
  if (typeof document === "undefined") return;
  const existing = document.getElementById(id);
  if (existing) {
    if (existing.textContent !== css) existing.textContent = css; // self-heal
    return;
  }
  const el = document.createElement("style");
  el.id = id;
  el.textContent = css;
  document.head.appendChild(el);
}

// ── Shared Perry keyframes ───────────────────────────────────
const PERRY_UI_KEYFRAMES = `
@keyframes perryEmberRise {
  0%   { transform: translateY(0) translateX(0) scale(1); opacity: 0; }
  10%  { opacity: 0.9; }
  100% { transform: translateY(-140px) translateX(var(--drift, 12px)) scale(0.35); opacity: 0; }
}
@keyframes perryPulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.45; }
}
@keyframes perryFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes perryRiseIn {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes perryShimmerSweep {
  0%   { transform: translateX(-120%) skewX(-18deg); }
  100% { transform: translateX(220%) skewX(-18deg); }
}
`;
export const ensurePerryUIKeyframes = () =>
  ensureKeyframes("perry-ui-keyframes", PERRY_UI_KEYFRAMES);

// ── EmberDust — drifting sparks in the glass ─────────────────
// Perry's answer to Terror's StarDust. Deterministic per index —
// no Math.random in render paths (seeded-worldgen law applies to
// chrome too).
const emberSeed = (i, k) => {
  // tiny deterministic hash → 0..1
  let a = (i * 2654435761 + k * 40503) >>> 0;
  a ^= a >>> 13; a = (a * 1274126177) >>> 0;
  return (a >>> 8) / 16777216;
};

export function EmberDust({ count = 12, tint = "#f0984a" }) {
  ensurePerryUIKeyframes();
  const motes = [];
  for (let i = 0; i < count; i++) {
    const left = 4 + emberSeed(i, 1) * 92;
    const bottom = emberSeed(i, 2) * 40;
    const size = 1.5 + emberSeed(i, 3) * 2.2;
    const dur = 4.5 + emberSeed(i, 4) * 5;
    const delay = emberSeed(i, 5) * 6;
    const drift = (emberSeed(i, 6) - 0.5) * 46;
    motes.push(
      <span
        key={i}
        style={{
          position: "absolute",
          left: `${left}%`,
          bottom: `${bottom}%`,
          width: size,
          height: size,
          borderRadius: "50%",
          background: tint,
          boxShadow: `0 0 ${size * 3}px ${tint}`,
          opacity: 0,
          animation: `perryEmberRise ${dur}s linear ${delay}s infinite`,
          "--drift": `${drift}px`,
          pointerEvents: "none",
        }}
      />
    );
  }
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      {motes}
    </div>
  );
}

// ── CinematicLayer — letterbox + vignette + grain ────────────
// Always-on during the run and the boot. `bars` slides the
// letterbox in/out.
const GRAIN_SVG = encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='160' height='160' filter='url(%23n)' opacity='0.5'/></svg>`
);

export function CinematicLayer({ bars = true, grain = true, vignette = true, barPx = 54, zIndex = 60 }) {
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex }}>
      {vignette && (
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse at 50% 50%, transparent 52%, rgba(0,0,0,0.55) 100%)",
        }} />
      )}
      {grain && (
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `url("data:image/svg+xml,${GRAIN_SVG}")`,
          opacity: 0.05,
          mixBlendMode: "overlay",
        }} />
      )}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0,
        height: bars ? barPx : 0, background: "#000",
        transition: "height 0.8s cubic-bezier(0.4,0,0.2,1)",
      }} />
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        height: bars ? barPx : 0, background: "#000",
        transition: "height 0.8s cubic-bezier(0.4,0,0.2,1)",
      }} />
    </div>
  );
}

// ── MetalRule — the copper divider under headers ─────────────
export function MetalRule({ width = "62%", style = {} }) {
  return (
    <div style={{
      position: "relative", height: 2, margin: "0 auto",
      width,
      background: "linear-gradient(90deg, transparent, #8e4a16 12%, #f0984a 35%, #ffbf70 50%, #f0984a 65%, #8e4a16 88%, transparent)",
      boxShadow: `0 0 8px ${GM.inkGoldBright}55`,
      ...style,
    }} />
  );
}

// ── KeyGlyph — a keyboard key rendered as forged chrome ──────
// Used by the QTE overlay, the Teachings page, and Options.
export function KeyGlyph({ k, size = 44, active = false, tint = GM.inkGoldBright }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size,
      borderRadius: Math.max(6, size * 0.16),
      background: active
        ? "linear-gradient(175deg, #4a2810 0%, #301a0b 55%, #1c1007 100%)"
        : "linear-gradient(175deg, #28170c 0%, #1c1009 55%, #100906 100%)",
      border: `2px solid ${active ? tint : GM.pageBorder}`,
      boxShadow: active
        ? `0 0 ${size * 0.4}px ${tint}66, inset 0 1px 0 rgba(255,191,112,0.5)`
        : "inset 0 1px 0 rgba(232,236,239,0.12), 0 3px 8px rgba(0,0,0,0.6)",
      color: active ? "#ffe6c2" : GM.vellumDim,
      fontFamily: GM.fontUI, fontWeight: 800,
      fontSize: size * 0.44, letterSpacing: 1,
      textShadow: active ? `0 0 8px ${tint}` : "0 1px 2px #000",
      transition: "all 0.12s ease",
    }}>
      {k}
    </span>
  );
}

// ── StatPip — Anima hearts / Chao flames for the HUD ─────────
export function StatPips({ value, max, full, empty, glyph = "❤", size = 15 }) {
  const pips = [];
  for (let i = 0; i < max; i++) {
    const lit = i < value;
    pips.push(
      <span key={i} style={{
        fontSize: size,
        color: lit ? full : empty,
        textShadow: lit ? `0 0 8px ${full}` : "none",
        opacity: lit ? 1 : 0.35,
        transition: "all 0.25s ease",
      }}>{glyph}</span>
    );
  }
  return <span style={{ display: "inline-flex", gap: 3 }}>{pips}</span>;
}
